export interface TemplateKeyRow {
    key: string;
    value?: string;
}

export const wrapTemplateKey = (name: string): string => `{${String(name || '').trim()}}`;

/** Dots in keys (e.g. solar.powerKw) break in Quill/HTML; use underscore tokens in documents. */
export const toSafeTemplateKeyName = (name: string): string =>
    String(name || '')
        .trim()
        .replace(/\./g, '_');

export const getModeFieldKey = (
    field: { i18nKey?: unknown; title?: unknown } | null | undefined,
    idx: number,
): string => {
    const base = String(field?.i18nKey || field?.title || `field_${idx + 1}`).trim();
    return base || `field_${idx + 1}`;
};

export const parseJsonObject = (raw: unknown): Record<string, unknown> | null => {
    if (!raw) return null;
    if (typeof raw === 'object' && !Array.isArray(raw)) return raw as Record<string, unknown>;
    if (typeof raw !== 'string') return null;

    try {
        let parsed: unknown = JSON.parse(raw);
        // Some rows arrive double-encoded as a JSON string.
        if (typeof parsed === 'string') {
            parsed = JSON.parse(parsed);
        }
        return parsed && typeof parsed === 'object' && !Array.isArray(parsed)
            ? (parsed as Record<string, unknown>)
            : null;
    } catch (_e) {
        return null;
    }
};

/** Flattens nested specification objects into dotted keys (and keeps leaf values). */
export const flattenDetailsEntries = (
    details: Record<string, unknown>,
    prefix = '',
): Array<[string, unknown]> => {
    const entries: Array<[string, unknown]> = [];
    Object.entries(details || {}).forEach(([key, value]) => {
        const path = prefix ? `${prefix}.${key}` : String(key || '').trim();
        if (!path) return;
        if (value && typeof value === 'object' && !Array.isArray(value)) {
            entries.push(...flattenDetailsEntries(value as Record<string, unknown>, path));
            return;
        }
        entries.push([path, value]);
    });
    return entries;
};

/** Resolves object record from vObject-like payload or plain object. */
export const getObjectRecord = (objectData: unknown): Record<string, unknown> | null => {
    if (!objectData || typeof objectData !== 'object' || Array.isArray(objectData)) return null;
    const root = objectData as Record<string, unknown>;
    if (root.object && typeof root.object === 'object' && !Array.isArray(root.object)) {
        return root.object as Record<string, unknown>;
    }
    return root;
};

/** objectDelails may arrive as camelCase or snake_case depending on API mapping. */
export const getObjectDelailsRaw = (objectRecord: Record<string, unknown> | null): unknown => {
    if (!objectRecord) return null;
    if (objectRecord.objectDelails != null && objectRecord.objectDelails !== '') {
        return objectRecord.objectDelails;
    }
    if (objectRecord.object_delails != null && objectRecord.object_delails !== '') {
        return objectRecord.object_delails;
    }
    return objectRecord.objectDelails ?? objectRecord.object_delails ?? null;
};

export const hasObjectField = (objectRecord: Record<string, unknown> | null, field: string): boolean => {
    if (!objectRecord || !(field in objectRecord)) return false;
    const value = objectRecord[field];
    if (value === null || value === undefined || value === '') return false;
    return true;
};
